import { toDisplayName } from '@web-app/utils/format.utils';
import { differenceInCalendarDays, isToday, isYesterday } from 'date-fns';
import type { Channel as ChannelType } from 'stream-chat';

/**
 * Extracts up to two initials from a name string (e.g. "Jane Doe" → "JD").
 * Used by avatar fallbacks in ChatChannelPreview and ChatHeader.
 */
export function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

/**
 * Resolves the display name for the other participant in a 1:1 channel.
 *
 * HFY sets channel.data.name to the patient's email/username during channel
 * creation (Django's user.username). The SDK's displayTitle prefers this over
 * member profiles, so we bypass it and resolve names from members directly.
 *
 * Fallback chain:
 * 1. Other member's user.name (from GetStream user profile)
 * 2. Other member's first_name + last_name (custom fields on profile)
 * 3. channel.data.client.name (HFY custom field set during channel creation)
 * 4. channel.data.name (often the email — last resort)
 * 5. user_id or 'Unknown'
 */
export function getOtherMemberName(
  channel: ChannelType,
  currentUserId?: string,
): string {
  const members = Object.values(channel.state.members);
  const other = members.find((m) => m.user_id !== currentUserId);
  const user = other?.user;

  // 1. User profile name (set via connectUser/upsertUser)
  if (user?.name) return toDisplayName(user.name);

  // 2. first_name + last_name custom fields (HFY sets these separately)
  const userRecord = user as unknown as Record<string, unknown> | undefined;
  const firstName = userRecord?.first_name as string | undefined;
  const lastName = userRecord?.last_name as string | undefined;
  if (firstName || lastName)
    return toDisplayName([firstName, lastName].filter(Boolean).join(' '));

  // 3. HFY custom field: channel.data.client.name
  const clientData = (channel.data as Record<string, unknown>)?.client as
    | { name?: string }
    | undefined;
  if (clientData?.name) return toDisplayName(clientData.name);

  // 4. Channel name (may be email — last resort before ID)
  const channelName = (channel.data as Record<string, unknown>)?.name as
    | string
    | undefined;
  if (channelName) return channelName;

  // 5. User ID
  return other?.user_id ?? 'Unknown';
}

/** Formats a date for channel preview timestamps (e.g. "2:30 PM", "Yesterday", "Monday"). */
export function formatPreviewTimestamp(date: Date): string {
  if (isToday(date)) {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }
  if (isYesterday(date)) return 'Yesterday';
  if (differenceInCalendarDays(new Date(), date) < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  });
}
